import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const EMPTY_ROW = { lot: '', raison_sociale: '', siret: '', montant_devis_ht: '', assurance_compagnie: '', assurance_numero_police: '', assurance_date_effet: '', assurance_date_fin: '' };

const RemarkBadge = ({ remarques }) => {
  if (!remarques) return <span className="text-slate-400 text-xs">Enregistrez pour contrôler</span>;
  if (!remarques.length) return <span className="text-emerald-600 text-xs font-medium">✓ Complet</span>;
  return (
    <div className="space-y-0.5">
      {remarques.map((r) => <p key={r} className="text-red-600 text-xs font-medium">⚠ {r}</p>)}
    </div>
  );
};

const Cell = ({ value, onChange, testId, type = 'text', className = '' }) => (
  <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`h-8 text-xs ${className}`} placeholder="—" data-testid={testId} title={String(value ?? '')} />
);

const toIso = (v) => (v && /^\d{2}\/\d{2}\/\d{4}$/.test(v) ? v.split('/').reverse().join('-') : (v || '').slice(0, 10));

export const LotsTable = ({ rows, onChange }) => {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v, remarques: undefined } : r)));
  const remove = (i) => window.confirm('Supprimer ce lot ?') && onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2" data-testid="ia-lots-table">
      <h5 className="text-sm font-semibold text-slate-700">Récapitulatif des entreprises par lot ({rows.length})</h5>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="text-left p-2 w-32">Lot</th><th className="text-left p-2 w-44">Entreprise / SIRET</th>
              <th className="text-left p-2 w-28">Montant devis (€ HT)</th><th className="text-left p-2 w-36">Assurance / organisme</th>
              <th className="text-left p-2 w-36">N° police / Réf. solidaire</th><th className="text-left p-2 w-36">Période de validité</th>
              <th className="text-left p-2">Remarques</th><th className="p-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.map((it, i) => (
              <tr key={i} className="border-t border-slate-100 align-top" data-testid={`ia-lot-row-${i}`}>
                <td className="p-2"><Cell value={it.lot} onChange={(v) => set(i, 'lot', v)} testId={`ia-lot-lot-${i}`} /></td>
                <td className="p-2 space-y-1">
                  <Cell value={it.raison_sociale} onChange={(v) => set(i, 'raison_sociale', v)} testId={`ia-lot-entreprise-${i}`} />
                  <Cell value={it.siret} onChange={(v) => set(i, 'siret', v)} testId={`ia-lot-siret-${i}`} className="text-[11px] text-slate-500" />
                </td>
                <td className="p-2"><Cell value={it.montant_devis_ht} onChange={(v) => set(i, 'montant_devis_ht', v)} testId={`ia-lot-montant-${i}`} className="text-right" /></td>
                <td className="p-2"><Cell value={it.assurance_compagnie} onChange={(v) => set(i, 'assurance_compagnie', v)} testId={`ia-lot-assureur-${i}`} /></td>
                <td className="p-2"><Cell value={it.assurance_numero_police || it.assurance_reference_solidaire} onChange={(v) => set(i, it.assurance_numero_police || !it.assurance_reference_solidaire ? 'assurance_numero_police' : 'assurance_reference_solidaire', v)} testId={`ia-lot-police-${i}`} /></td>
                <td className="p-2 space-y-1">
                  <Cell type="date" value={toIso(it.assurance_date_effet)} onChange={(v) => set(i, 'assurance_date_effet', v)} testId={`ia-lot-debut-${i}`} />
                  <Cell type="date" value={toIso(it.assurance_date_fin)} onChange={(v) => set(i, 'assurance_date_fin', v)} testId={`ia-lot-fin-${i}`} />
                </td>
                <td className="p-2" data-testid={`ia-lot-remarques-${i}`}><RemarkBadge remarques={it.remarques} /></td>
                <td className="p-2">
                  <button onClick={() => remove(i)} className="text-slate-300 hover:text-red-500" aria-label="Supprimer le lot" data-testid={`ia-lot-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onChange([...rows, { ...EMPTY_ROW }])} className="gap-1 text-violet-700" data-testid="ia-lot-add-btn">
        <Plus className="h-4 w-4" /> Ajouter un lot
      </Button>
    </div>
  );
};
